import React, { useEffect, useState, useCallback } from 'react'
import { Button } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CrearAsignacionForm from '../components/CrearAsignacionForm';
import socket from "../../socket";
import { apiFetch } from '../../api';
import { getUserContext } from '../../auth';

const LIMIT = 20;

export default function CrearAsignacion({ openControlled = undefined, onCloseControlled = undefined, initialSelectedClients = [], showTriggerButton = true }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [vendedores, setVendedores] = useState([])
  
  // Estado de Clientes
  const [clientes, setClientes] = useState([])
  const [totalClientes, setTotalClientes] = useState(0) // 👈 NUEVO ESTADO PARA EL TOTAL REAL
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  const [selectedVendedor, setSelectedVendedor] = useState('')
  const [selectedClientes, setSelectedClientes] = useState([]) 

  const [loading, setLoading] = useState(false)
  const [loadingLists, setLoadingLists] = useState(false) 
  const [error, setError] = useState(null)

  const isControlled = typeof openControlled === 'boolean'
  const open = isControlled ? openControlled : internalOpen

  const handleOpen = () => {
    if (!isControlled) setInternalOpen(true);
  }

  const handleClose = () => {
    if (loading) return;
    if (isControlled) {
      if (typeof onCloseControlled === 'function') onCloseControlled();
      return;
    }
    setInternalOpen(false);
    setError(null);
    setSelectedVendedor('');
    setSelectedClientes([]);
    setClientes([]); 
    setTotalClientes(0);
  }

  // Función genérica para cargar clientes
  const fetchClientesPage = useCallback(async (pageNum) => {
    const params = new URLSearchParams();
    params.set('page', String(pageNum));
    params.set('limit', String(LIMIT));
    
    const res = await apiFetch(`/api/consultas_sin_asignacion?${params.toString()}`, {
      credentials: "include"
    });
    
    if (!res.ok) throw new Error("Error fetching consultas");
    const data = await res.json();
    
    const newItems = Array.isArray(data.items) ? data.items : [];
    const total = data.total || 0;
    
    return { newItems, total };
  }, []);

  // 1. Cargar MÁS clientes
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const { newItems, total } = await fetchClientesPage(nextPage);
      
      setClientes(prev => [...prev, ...newItems]);
      setTotalClientes(total); // 👈 Actualizamos el total por si cambió
      setPage(nextPage);
      
      if (newItems.length < LIMIT || (clientes.length + newItems.length) >= total) {
        setHasMore(false);
      }
    } catch (e) {
      console.error("Error loading more clients:", e);
    } finally {
      setLoadingMore(false);
    }
  };

  // 2. Recarga completa
  const reloadAll = useCallback(async () => {
    if (!open) return;
    try {
      const { newItems, total } = await fetchClientesPage(1);
      setClientes(newItems);
      setTotalClientes(total); // 👈 Guardamos el total inicial
      setPage(1);
      setHasMore(newItems.length < total);
    } catch (e) {
      console.error("❌ Error recargando consultas:", e);
    }
  }, [open, fetchClientesPage]);

  // Carga inicial
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      setLoadingLists(true);
      try {
        setPage(1);
        setHasMore(true);
        setClientes([]);
        setTotalClientes(0);

        const [resV, dataC] = await Promise.all([
          apiFetch(`/api/vendedores/listado?ocultar_inactivos=true&limit=1000`, { credentials: "include" }),
          fetchClientesPage(1)
        ]);

        if (!resV.ok) throw new Error("Error fetching vendedores");
        
        const dataV = await resV.json();
        
        if (!mounted) return;
        
        setVendedores(Array.isArray(dataV.items) ? dataV.items : []);
        setClientes(dataC.newItems);
        setTotalClientes(dataC.total); // 👈 Guardamos el total inicial
        setHasMore(dataC.newItems.length < dataC.total);

      } catch (e) {
        if (!mounted) return;
        setError(e.message || "No se pudieron cargar las listas");
      } finally {
        if (mounted) setLoadingLists(false);
      }
    })();
    return () => { mounted = false; };
  }, [open, fetchClientesPage]);

  // Sync initial selections
  useEffect(() => {
    try {
      if (Array.isArray(initialSelectedClients) && initialSelectedClients.length > 0) {
        setSelectedClientes(initialSelectedClients.map(Number));
      }
    } catch (err) {
      console.error('CrearAsignacion useEffect error:', err)
    }
  }, [initialSelectedClients]);

  // Sockets
  useEffect(() => {
    if (!open) return;
    const handleUpdate = (data) => {
      const user = getUserContext();
      if (user && String(data?.empresa_id) !== String(user.empresa_id)) return;
      reloadAll(); 
    };

    socket.on("create_cliente", handleUpdate);
    socket.on("update_cliente", handleUpdate);
    socket.on("create_asignacion", handleUpdate);

    return () => {
      socket.off("create_cliente", handleUpdate);
      socket.off("update_cliente", handleUpdate);
      socket.off("create_asignacion", handleUpdate);
    };
  }, [reloadAll, open]);

  // Listen for vendor changes (triggered after alta/baja) and reload vendedores
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    const handler = async () => {
      try {
        const resV = await apiFetch(`/api/vendedores/listado?ocultar_inactivos=true&limit=1000`, { credentials: 'include' });
        if (!resV.ok) return;
        const dataV = await resV.json();
        if (!mounted) return;
        setVendedores(Array.isArray(dataV.items) ? dataV.items : []);
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener('vendedores_changed', handler);
    return () => { mounted = false; window.removeEventListener('vendedores_changed', handler); };
  }, [open]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError(null);

    const clientesToAssign = Array.isArray(selectedClientes) ? selectedClientes.map(Number) : [];
    if (!selectedVendedor || clientesToAssign.length === 0) {
      setError("Seleccioná 1 vendedor y al menos 1 consulta");
      return;
    }

    setLoading(true);
    try {
      const res = await apiFetch(`/api/asignaciones/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_vendedor: Number(selectedVendedor),
          ids_clientes: clientesToAssign,
        }),
        credentials: "include",
      });
      if (!res.ok) {
        let msg = "Error creando asignaciones";
        try { const j = await res.json(); msg = j.detail || JSON.stringify(j); }
        catch { msg = await res.text() || msg; }
        throw new Error(msg);
      }

      setClientes(prev => prev.filter(c => !clientesToAssign.includes(Number(c.id))));
      // Restamos los asignados del total visualmente para que baje el contador
      setTotalClientes(prev => Math.max(0, prev - clientesToAssign.length));
      
      setSelectedClientes([]);
      setSelectedVendedor('');
      handleClose();
    } catch (err) {
      setError(err.message || "Error creando asignaciones");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showTriggerButton && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpen}
          sx={{
            minHeight: 36,
            px: 2,
            borderRadius: '8px',
            fontSize: 12,
            fontWeight: 800,
            textTransform: 'none',
            backgroundColor: '#0059f6',
            boxShadow: '0 10px 22px rgba(0, 89, 246, 0.22)',
            '&:hover': {
              backgroundColor: '#004be0',
              boxShadow: '0 12px 26px rgba(0, 89, 246, 0.28)',
            },
          }}
        >
          Crear asignación
        </Button>
      )}

      <CrearAsignacionForm
        open={open}
        onClose={handleClose}
        vendedores={vendedores}
        clientes={clientes}
        selectedVendedor={selectedVendedor}
        selectedClientes={selectedClientes}
        setSelectedVendedor={setSelectedVendedor}
        setSelectedClientes={setSelectedClientes}
        loading={loading}
        loadingLists={loadingLists}
        error={error}
        onSubmit={handleSubmit}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        loadingMore={loadingMore}
        totalCount={totalClientes} // 👈 Pasamos el total al componente visual
      />
    </>
  );
}
